import { supabase } from '../lib/supabase';

const BUCKET_NAME = 'vetrine-images';

/**
 * Upload a public image to vetrine-images bucket
 * @param {File} file - The file to upload
 * @param {string} folder - Folder path (e.g., 'logos', 'main', 'gallery')
 * @returns {Promise<{success: boolean, filePath?: string, publicUrl?: string, error?: string}>}
 */
export const uploadVetrineImage = async (file, folder = 'uploads') => {
  try {
    if (!file) {
      return { success: false, error: 'Nessun file fornito' };
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes?.includes(file?.type)) {
      return { success: false, error: 'Tipo file non supportato. Usa JPG, PNG o WebP' };
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file?.size > maxSize) {
      return { success: false, error: 'File troppo grande. Massimo 10MB' };
    }

    // Generate unique filename
    const timestamp = Date.now();
    const randomStr = Math.random()?.toString(36)?.substring(2, 8);
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${timestamp}-${randomStr}.${fileExt}`;
    const filePath = `${folder}/${fileName}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase?.storage?.from(BUCKET_NAME)?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Upload error:', error);
      return { success: false, error: error?.message };
    }

    // Get public URL
    const { data: urlData } = supabase?.storage?.from(BUCKET_NAME)?.getPublicUrl(filePath);

    return {
      success: true,
      filePath: data?.path,
      publicUrl: urlData?.publicUrl
    };
  } catch (err) {
    console.error('Exception during upload:', err);
    return { success: false, error: err?.message };
  }
};

/**
 * Delete an image from vetrine-images bucket
 * @param {string} filePath - The file path to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteVetrineImage = async (filePath) => {
  try {
    if (!filePath) {
      return { success: false, error: 'Nessun percorso file fornito' };
    }

    const { error } = await supabase?.storage?.from(BUCKET_NAME)?.remove([filePath]);

    if (error) {
      console.error('Delete error:', error);
      return { success: false, error: error?.message };
    }

    return { success: true };
  } catch (err) {
    console.error('Exception during delete:', err);
    return { success: false, error: err?.message };
  }
};

/**
 * Get public URL for an image
 * @param {string} filePath - The file path
 * @returns {string} Public URL
 */
export const getVetrineImageUrl = (filePath) => {
  if (!filePath) return null;
  
  const { data } = supabase?.storage?.from(BUCKET_NAME)?.getPublicUrl(filePath);
  
  return data?.publicUrl;
};

export default {
  uploadVetrineImage,
  deleteVetrineImage,
  getVetrineImageUrl
};